import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPatientById } from '../api/patientApi';

/**
 * Resolves the patient from the URL.
 *
 * Every assessment screen used to read `location.state.patientId`, so a refresh
 * or a shared link silently dropped the patient and the clinician carried on
 * scoring against nobody. Patient identity now comes from the route, and this
 * hook fetches the record so no screen depends on navigation state surviving.
 *
 * Falls back to the external Vqura ORDS directory for patients that originated
 * there (patientApi already degrades to null if that host is unreachable).
 */
export const usePatient = () => {
  const { patientId } = useParams();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!patientId) {
      setLoading(false);
      setError('No patient specified.');
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError('');

    (async () => {
      try {
        const res = await getPatientById(patientId);
        const local = res?.data?.succeeded ? res.data.data : null;

        if (local) {
          if (!cancelled) setPatient({ ...local, source: 'local' });
        } else if (!cancelled) {
          setError('That patient could not be found.');
        }
      } catch (e) {
        if (!cancelled) setError('Could not load the patient.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [patientId]);

  // setPatient is exposed so a screen that edits the demographics can push the
  // saved values straight back in, instead of refetching a record it just wrote.
  return { patientId, patient, setPatient, patientName: patient?.name || '', loading, error };
};

/** Whole years, the unit clinicians use when talking about developmental stage. */
export const ageInYears = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - dob.getFullYear();
  const beforeBirthday =
    now.getMonth() < dob.getMonth() ||
    (now.getMonth() === dob.getMonth() && now.getDate() < dob.getDate());
  if (beforeBirthday) years -= 1;
  return years >= 0 ? years : null;
};

/** "4y 3m" — months matter for young children, where a year is a huge span. */
export const formatAge = (dateOfBirth) => {
  if (!dateOfBirth) return '—';
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return '—';

  const now = new Date();
  let months = (now.getFullYear() - dob.getFullYear()) * 12 + (now.getMonth() - dob.getMonth());
  if (now.getDate() < dob.getDate()) months -= 1;
  if (months < 0) return '—';

  const years = Math.floor(months / 12);
  const rem = months % 12;
  return years === 0 ? `${rem}m` : `${years}y ${rem}m`;
};
